import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { useLazyGetUserSocialOrdersPageQuery } from '../../../../../api/socialOrders'
import type { TGetSocialOrdersCursorQueryParams } from '../../../../../api/types/socialOrders'
import { PageLayout } from '../../../../../components/PageLayout'
import { SocialOrdersList } from '../../../../../components/SocialOrders/SocialOrdersList'
import { NotFound } from '../../../../../components/ui-kit/NotFound'
import { useSocialOrdersWindowedPage } from '../../../../../hooks/useSocialOrdersWindowedPage'

export const UserSocialOrders = () => {
  const { id } = useParams<{ id: string }>()
  const userId = useMemo(() => (id ? parseInt(id, 10) : NaN), [id])
  const queryArgs = useMemo(() => ({}), [])
  const resetKey = useMemo(() => `user:${userId}`, [userId])
  const [loadUserSocialOrdersPage] = useLazyGetUserSocialOrdersPageQuery()

  const loadPage = useCallback(
    (arg: TGetSocialOrdersCursorQueryParams, preferCacheValue?: boolean) =>
      loadUserSocialOrdersPage({ userId, ...arg }, preferCacheValue),
    [loadUserSocialOrdersPage, userId],
  )

  const { items, hasMore, isInitialLoading, isFetchingMore, isError, loadMore } =
    useSocialOrdersWindowedPage(resetKey, loadPage, queryArgs)

  if (Number.isNaN(userId)) {
    return <NotFound text="Пользователь не найден" />
  }

  if (isInitialLoading && items.length === 0) {
    return (
      <PageLayout
        title="Соц-заказы которые пользователь создал"
        mainComponent={(
          <Stack alignItems="center" justifyContent="center" minHeight="100%">
            <CircularProgress />
          </Stack>
        )}
      />
    )
  }

  if (isError) {
    return (
      <PageLayout
        title="Соц-заказы которые пользователь создал"
        mainComponent={(
          <Typography color="error" textAlign="center" px={2}>
            Не удалось загрузить социальные заказы пользователя
          </Typography>
        )}
      />
    )
  }

  return (
    <PageLayout
      title="Соц-заказы которые пользователь создал"
      mainComponent={(
        <SocialOrdersList
          socialOrders={items}
          hasMore={hasMore}
          isLoadingMore={isFetchingMore}
          onLoadMore={loadMore}
          emptyMessage="У пользователя пока нет социальных заказов"
        />
      )}
    />
  )
}
